<template>
  <div class="recommandations-page mandat-page">
    <header class="mandat-page__hero">
      <p class="mandat-page__eyebrow">Mandat</p>
      <h1 class="mandat-page__title">
        {{ isEditing ? 'Modifier la recommandation' : (recommandationId ? 'Détail de la recommandation' : 'Nouvelle recommandation') }}
      </h1>
      <p class="mandat-page__subtitle">
        Informations générales, actions client, actions conseiller et matrice RACI.
      </p>
    </header>

    <div class="mandat-form-card">
      <form @submit.prevent="saveRecommendation">
        <div class="mb-4">
          <h2 class="mandat-form-section__title mb-3">Informations générales</h2>
            <div class="mb-3">
              <label for="titre" class="form-label">Titre*</label>
              <input type="text" class="form-control" id="titre" v-model="formData.titre" :readonly="isReadOnly" required>
            </div>
            <div class="mb-3">
              <label for="description" class="form-label">Description</label>
              <textarea class="form-control" id="description" v-model="formData.description" rows="3" :readonly="isReadOnly"></textarea>
            </div>
            <div class="mb-3">
              <label for="date_echeance" class="form-label">Date d'échéance*</label>
              <input type="date" class="form-control" id="date_echeance" v-model="formData.date_echeance" :readonly="isReadOnly" required>
            </div>
            <div class="mb-3">
              <label for="client" class="form-label">Client*</label>
              <select class="form-select" id="client" v-model="formData.client_login" :disabled="isReadOnly" required>
                <option value="">Sélectionner un client</option>
                <option v-for="client in clients" :key="client.login || client.email" :value="client.login">
                  {{ clientLabel(client) }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label for="statut_reco" class="form-label">Statut de la recommandation</label>
              <select class="form-select" id="statut_reco" v-model="formData.statut" :disabled="isReadOnly && !canValidate">
                <option value="non_commencee">Non commencée</option>
                <option value="en_cours">En cours</option>
                <option value="terminee">Terminée</option>
                <option value="en_attente">En attente</option>
                <option value="annulee">Annulée</option>
              </select>
            </div>
          </div>

          <!-- Actions du client -->
          <div class="mb-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2 class="mandat-form-section__title mb-0">Actions du client</h2>
              <button v-if="!isReadOnly" type="button" class="btn btn-sm btn-primary" @click="addClientAction">
                <i class="fas fa-plus me-1"></i> Ajouter
              </button>
            </div>

            <div v-for="(action, index) in formData.actionsClient" :key="'client-' + index" class="card mandat-action-card mb-3">
              <div class="card-body">
                <div class="d-flex justify-content-between mb-3">
                  <h3 class="mandat-action-card__title">Action #{{ index + 1 }}</h3>
                  <button v-if="!isReadOnly" type="button" class="btn btn-sm btn-danger" @click="removeClientAction(index)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>

                <div class="mb-3">
                  <label :for="'client_action_titre_' + index" class="form-label">Description*</label>
                  <input type="text" class="form-control" :id="'client_action_titre_' + index" v-model="action.titre"
                    :readonly="isReadOnly" required>
                </div>

                <div class="mb-3">
                  <label :for="'client_action_date_' + index" class="form-label">Date d'échéance*</label>
                  <input type="date" class="form-control" :id="'client_action_date_' + index"
                    v-model="action.date_echeance" :readonly="isReadOnly" required>
                </div>
                <div class="mb-3">
                  <label :for="'client_action_statut_' + index" class="form-label">Statut*</label>
                  <select class="form-select" :id="'client_action_statut_' + index" v-model="action.statut" :disabled="isReadOnly" required>
                    <option value="non_commencee">Non commencée</option>
                    <option value="en_cours">En cours</option>
                    <option value="terminee">Terminée</option>
                    <option value="en_attente">En attente</option>
                    <option value="annulee">Annulée</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Actions du conseiller -->
          <div class="mb-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2 class="mandat-form-section__title mb-0">Actions du conseiller</h2>
              <button v-if="!isReadOnly" type="button" class="btn btn-sm btn-primary" @click="addAdvisorAction">
                <i class="fas fa-plus me-1"></i> Ajouter
              </button>
            </div>

            <div v-for="(action, index) in formData.actions" :key="'advisor-' + index" class="card mandat-action-card mb-3">
              <div class="card-body">
                <div class="d-flex justify-content-between mb-3">
                  <h3 class="mandat-action-card__title">Action #{{ index + 1 }}</h3>
                  <button v-if="!isReadOnly" type="button" class="btn btn-sm btn-danger" @click="removeAdvisorAction(index)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>

                <div class="mb-3">
                  <label :for="'action_titre_' + index" class="form-label">Description*</label>
                  <input type="text" class="form-control" :id="'action_titre_' + index" v-model="action.titre" :readonly="isReadOnly" required>
                </div>

                <div class="mb-3">
                  <label :for="'action_date_' + index" class="form-label">Date d'échéance*</label>
                  <input type="date" class="form-control" :id="'action_date_' + index" v-model="action.date_echeance"
                    :readonly="isReadOnly" required>
                </div>
                <div class="mb-3">
                  <label :for="'client_action_statut_' + index" class="form-label">Statut*</label>
                  <select class="form-select" :id="'client_action_statut_' + index" v-model="action.statut" :disabled="isReadOnly" required>
                    <option value="non_commencee">Non commencée</option>
                    <option value="en_cours">En cours</option>
                    <option value="terminee">Terminée</option>
                    <option value="en_attente">En attente</option>
                    <option value="annulee">Annulée</option>
                  </select>
                </div>

                <!-- RACI -->
                <div class="row">
                  <div class="col-md-3">
                    <div class="mb-3">
                      <label :for="'action_responsable_' + index" class="form-label">Responsable (R)*</label>
                      <select class="form-select" :id="'action_responsable_' + index" v-model="action.responsable_id"
                        :disabled="isReadOnly" required>
                        <option value="">Sélectionner</option>
                        <option v-for="employe in employes" :key="employe.id" :value="employe.id">
                          {{ employe.nom }} {{ employe.prenom }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="mb-3">
                      <label :for="'action_accountable_' + index" class="form-label">Accountable (A)*</label>
                      <select class="form-select" :id="'action_accountable_' + index" v-model="action.accountable_id"
                        :disabled="isReadOnly" required>
                        <option value="">Sélectionner</option>
                        <option v-for="employe in employes" :key="employe.id" :value="employe.id">
                          {{ employe.nom }} {{ employe.prenom }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="mb-3">
                      <label :for="'action_consulted_' + index" class="form-label">Consulté (C)</label>
                      <select class="form-select" :id="'action_consulted_' + index" v-model="action.consulted_ids"
                        :disabled="isReadOnly" multiple>
                        <option v-for="employe in employes" :key="employe.id" :value="employe.id">
                          {{ employe.nom }} {{ employe.prenom }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="mb-3">
                      <label :for="'action_informed_' + index" class="form-label">Informé (I)</label>
                      <select class="form-select" :id="'action_informed_' + index" v-model="action.informed_ids"
                        :disabled="isReadOnly" multiple>
                        <option v-for="employe in employes" :key="employe.id" :value="employe.id">
                          {{ employe.nom }} {{ employe.prenom }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="mandat-form__actions">
            <button type="button" class="btn btn-secondary" @click="goBack">
              Annuler
            </button>
            <router-link v-if="isReadOnly && canEdit" :to="`/actions-mandat/actions-recommandations/${recommandationId}/modifier`" class="btn btn-primary">
              <i class="fas fa-edit me-1"></i> Modifier
            </router-link>
            <button v-else-if="isReadOnly && canValidate && !canEdit" type="submit" class="btn btn-primary" :disabled="loading">
              <i class="fas fa-check-circle me-1"></i> {{ loading ? 'Validation…' : 'Valider la recommandation' }}
            </button>
            <button v-else type="submit" class="btn btn-primary" :disabled="loading">
              {{ loading ? 'Enregistrement…' : isEditing ? 'Modifier' : 'Créer' }}
            </button>
          </div>
        </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { getUserRole, refreshProfileFromApi } from '@/utils/auth';
import { getProfileUserId } from '@/utils/sessionAuth';
import { canEditMandat, canValidateMandat, isMandatViewer } from '@/utils/permissions';

export default {
  name: 'DetailsRecommandations',
  data() {
    return {
      formData: {
        titre: '',
        description: '',
        client_login: '',
        statut: 'non_commencee',
        date_echeance: '',
        actionsClient: [],
        actions: []
      },
      clients: [],
      employes: [],
      userRole: null,
      currentUserId: null,
      isEditing: false,
      recommandationId: null,
      loading: false,
      error: null
    }
  },
  computed: {
    // Viewer (saisie) : toujours lecture seule sur une recommandation existante, peut valider
    // Éditeur (validation) : lecture seule si pas /modifier, peut aller modifier
    isReadOnly() {
      if (!this.recommandationId) return false;
      if (isMandatViewer()) return true;
      return !this.isEditing;
    },
    canEdit() {
      return canEditMandat();
    },
    canValidate() {
      return canValidateMandat();
    }
  },
  async created() {
    await refreshProfileFromApi();
    this.userRole = getUserRole();
    this.currentUserId = getProfileUserId() || null;

    if (!this.$route.params.id && isMandatViewer()) {
      this.$router.replace('/actions-mandat/client-recommandations');
      return;
    }

    if (this.$route.params.id) {
      this.recommandationId = this.$route.params.id;
      this.isEditing = this.$route.path.endsWith('/modifier');
      this.fetchRecommendation();
    }
    this.fetchClients();
    this.fetchEmployes();
  },
  watch: {
    '$route'(to) {
      if (to.params.id) {
        this.recommandationId = to.params.id;
        this.isEditing = to.path.endsWith('/modifier');
        this.fetchRecommendation();
      } else {
        this.isEditing = false;
        this.recommandationId = null;
        // Réinitialiser le formulaire si on passe en mode création
        this.formData = {
          titre: '',
          description: '',
          client_login: '',
          statut: 'non_commencee',
          date_echeance: '',
          actionsClient: [],
          actions: []
        };
      }
    }
  },
  methods: {
    async fetchClients() {
      try {
        const response = await axios.get("/api/clients-private/", {
        });

        const raw = Array.isArray(response.data) ? response.data : (response.data?.results || response.data?.data || []);
        this.clients = raw;
      } catch (error) {
        this.handleApiError(error, "Erreur lors du chargement des clients");
      }
    },

    clientLabel(client) {
      const name = [client?.prenom, client?.nom].filter(Boolean).join(' ').trim();
      return name || client?.login || client?.email || 'Client';
    },

    async fetchEmployes() {
      try {
        const response = await axios.get("/api/employes/", {
        });

        const raw = Array.isArray(response.data) ? response.data : (response.data?.results || response.data?.data || []);
        this.employes = raw;
      } catch (error) {
        this.handleApiError(error, "Erreur lors du chargement des employés");
      }
    },

    async fetchRecommendation() {
      try {
        const response = await axios.get(`/api/actions-recommandations/${this.recommandationId}/`, {
        });
        const data = response.data;
        
        // Correction du mapping pour tous les champs, y compris client et date_echeance
        this.formData = {
          titre: data.titre || '',
          description: data.description || '',
          client_login: data.client?.login || '',
          statut: data.statut || 'non_commencee',
          date_echeance: data.date_echeance || '',
          actionsClient: (data.raci_client || []).map(action => ({
            id: action.id, 
            titre: action.description || '',
            date_echeance: action.date_echeance || '',
            statut: action.statut || 'non_commencee',
            original_statut: action.statut || 'non_commencee', 
          })),
          actions: (data.raci_employe || []).map(action => ({
            id: action.id, 
            titre: action.description || '',
            date_echeance: action.date_echeance || '',
            statut: action.statut || 'non_commencee',
            original_statut: action.statut || 'non_commencee', 
            responsable_id: action.responsable_id || action.responsable?.id || '',
            accountable_id: action.accountable_id || action.accountable?.id || '',
            consulted_ids: (action.consulted_ids && action.consulted_ids.length)
              ? action.consulted_ids
              : (action.consulted?.map(emp => emp.id) || []),
            informed_ids: (action.informed_ids && action.informed_ids.length)
              ? action.informed_ids
              : (action.informed?.map(emp => emp.id) || []), 
          }))
        };

      } catch (error) {
        this.handleApiError(error, "Erreur lors du chargement de la recommandation");
      }
    },
    isActionEnRetard(action) {
      // Utilisez cette méthode pour les nouvelles actions créées côté frontend
      if (action.statut === 'terminee') return false;
      if (!action.date_echeance) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const echeance = new Date(action.date_echeance);

      return echeance < today;
    },

    removeClientAction(index) {
      this.formData.actionsClient.splice(index, 1);
    },

    addClientAction() {
      this.formData.actionsClient.push({
        titre: '',
        date_echeance: '',
        statut: 'non_commencee',
      });
    },
    addAdvisorAction() {
      this.formData.actions.push({
        titre: '',
        date_echeance: '',
        statut: 'non_commencee',
        responsable_id: '',
        accountable_id: '',
        consulted_ids: [],
        informed_ids: [],
      });
    },

    removeAdvisorAction(index) {
      this.formData.actions.splice(index, 1);
    },

    validateForm() {
      if (!this.formData.titre) {
        throw new Error("Le titre est requis");
      }
      if (!this.formData.client_login) {
        throw new Error("Le client est requis");
      }

      // Validation des actions client
      for (const [index, action] of this.formData.actionsClient.entries()) {
        if (!action.titre) {
          throw new Error(`Description requise pour l'action client #${index + 1}`);
        }
        if (!action.date_echeance) {
          throw new Error(`Date d'échéance requise pour l'action client #${index + 1}`);
        }
        if (!action.statut) {
          throw new Error(`Statut requis pour l'action client #${index + 1}`);
        }
      }

      // Validation des actions conseiller
      if (this.formData.actions.length === 0) {
        throw new Error("Au moins une action conseiller est requise");
      }

      for (const [index, action] of this.formData.actions.entries()) {
        if (!action.titre) {
          throw new Error(`Description requise pour l'action conseiller #${index + 1}`);
        }
        if (!action.date_echeance) {
          throw new Error(`Date d'échéance requise pour l'action conseiller #${index + 1}`);
        }
        if (!action.statut) {
          throw new Error(`Statut requis pour l'action conseiller #${index + 1}`);
        }
        if (!action.responsable_id) {
          throw new Error(`Responsable requis pour l'action conseiller #${index + 1}`);
        }
        if (!action.accountable_id) {
          throw new Error(`Accountable requis pour l'action conseiller #${index + 1}`);
        }

        // Validation des dates
        const dateEcheance = new Date(action.date_echeance);
        const dateAujourdhui = new Date();
        dateAujourdhui.setHours(0, 0, 0, 0);

        if (dateEcheance < dateAujourdhui) {
          throw new Error(`La date d'échéance de l'action conseiller #${index + 1} ne peut pas être dans le passé`);
        }
      }
    },

    async saveRecommendation() {
      this.loading = true;
      try {
        this.validateForm();

        const dataToSend = {
          titre: this.formData.titre,
          description: this.formData.description,
          client_login: this.formData.client_login,
          statut: this.formData.statut,
          date_echeance: this.formData.date_echeance,
          raci_client: this.formData.actionsClient.map(action => ({
            id: action.id, // Inclure l'ID pour les mises à jour/suppressions
            description: action.titre, 
            date_echeance: action.date_echeance,
            statut: action.statut,
          })),
          raci_employe: this.formData.actions.map(action => ({
            id: action.id, // Inclure l'ID pour les mises à jour/suppressions
            description: action.titre,
            date_echeance: action.date_echeance,
            statut: action.statut,
            responsable_id: action.responsable_id, // Utilisez le nouveau nom de champ pour les IDs
            accountable_id: action.accountable_id, // Utilisez le nouveau nom de champ pour les IDs
            consulted_ids: action.consulted_ids,    // Utilisez le nouveau nom de champ pour les IDs
            informed_ids: action.informed_ids       // Utilisez le nouveau nom de champ pour les IDs
          }))
        };

        const config = {};

        let response;
        if (this.isEditing) {
          response = await axios.put(
            `/api/actions-recommandations/${this.recommandationId}/`,
            dataToSend,
            config
          );
        } else {
          response = await axios.post(
            '/api/actions-recommandations/',
            dataToSend,
            config
          );
        }

        // Utilisation de la réponse
        console.log('Réponse du serveur:', response.data);

        await Swal.fire({
          title: 'Succès!',
          text: `La recommandation a été ${this.isEditing ? 'modifiée' : 'créée'} avec succès.`,
          icon: 'success',
          timer: 1700,
          showConfirmButton: false
        });

        this.$router.push('/actions-mandat/client-recommandations');
      } catch (error) {
        this.handleApiError(error, "Erreur lors de l'enregistrement");
      } finally {
        this.loading = false;
      }
    },

    handleApiError(error, customMessage = "Une erreur est survenue") {
      console.error("Erreur API:", error);
      
      if (error.response?.status === 401) {
        this.error = 'Session expirée. Veuillez vous reconnecter.';
        this.$router.push('/connexion');
        return;
      } else if (error.response) {
        this.error = error.response.data?.detail || customMessage;
      } else if (error.request) {
        this.error = "Impossible de contacter le serveur.";
      } else {
        this.error = customMessage;
      }
      Swal.fire('Erreur', this.error, 'error');
    },

    async goBack() {
      this.$router.push('/actions-mandat/client-recommandations');
    }
  }
}
</script>

<style scoped>
.mandat-form-card .row {
  --bs-gutter-x: 1rem;
}
</style>
